<style lang="scss" scoped>
.trial-wo-attachment-dialog-body {
  min-height: 100px;
  padding-bottom: 16px;
}

.trial-wo-attachment-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.trial-wo-attachment-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.trial-wo-upload-click /deep/ .el-upload__tip {
  margin-top: 6px;
}

.trial-wo-attachment-ops {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  gap: 2px;
}

.trial-wo-attachment-ops /deep/ .el-button {
  margin-left: 0;
  margin-right: 0;
  padding-left: 6px;
  padding-right: 6px;
}
</style>

<template>
  <el-dialog
    :title="$t('fujian')"
    :visible="visible"
    width="560px"
    append-to-body
    :close-on-click-modal="false"
    @close="onInnerClose"
  >
    <div v-loading="loading" class="trial-wo-attachment-dialog-body">
      <div v-if="!readOnly && utils.checkPower('web_test_mold_work_order_uploadAttachFile')" class="trial-wo-attachment-toolbar">
        <!-- 与 Element 文档「点击上传」示例一致：选文件后立即上传（auto-upload 默认 true） -->
        <el-upload
          ref="uploadRef"
          class="trial-wo-upload-click"
          action="#"
          :http-request="customUpload"
          :show-file-list="false"
          :before-upload="beforeUpload"
          :on-exceed="handleUploadExceed"
          :limit="uploadLimitRemaining"
          :accept="accept"
          :disabled="fileList.length >= maxAttachments"
          multiple
        >
          <el-button size="mini" type="primary" plain :disabled="fileList.length >= maxAttachments">
            点击上传
          </el-button>
          <div slot="tip" class="el-upload__tip trial-wo-attachment-hint">{{ formatHint }}</div>
        </el-upload>
      </div>

      <el-table
        :data="fileList"
        size="mini"
        border
        stripe
        :empty-text="''"
        style="width: 100%; margin-top: 12px;"
        max-height="320"
      >
        <template #empty><span></span></template>
        <el-table-column type="index" :label="$t('xuhao')" width="55"></el-table-column>
        <el-table-column prop="name" :label="$t('mingcheng')" min-width="160" show-overflow-tooltip></el-table-column>
        <el-table-column :label="$t('caozuo')" width="160" align="center">
          <template #default="{ row, $index }">
            <div class="trial-wo-attachment-ops">
              <el-button type="text" size="mini" @click="downloadFile(row)">{{ $t('xiazai') }}</el-button>
              <el-button v-if="!readOnly && utils.checkPower('web_test_mold_work_order_uploadAttachFile')" type="text" class="font-color-danger" size="mini" @click="removeAt($index, row)">
                {{ $t('shanchu') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button size="mini" @click="onInnerClose">{{ $t('guanbi') }}</el-button>
    </span>
  </el-dialog>
</template>

<script type="text/javascript">
export default {
  name: 'trial_mould_wo_attachment_dialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 试模工单表格行（与列表同一引用，更新后同步 attachFileIds） */
    row: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      maxAttachments: 20,
      loading: false,
      fileList: [],
      closeEmitted: false,
      accept:
        'image/png,image/jpeg,image/gif,image/webp,image/bmp,.jpg,.jpeg,.png,.gif,.webp,.bmp,application/pdf,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.doc,.docx'
    };
  },
  computed: {
    readOnly() {
      return this.row != null && Number(this.row.status) === 4;
    },
    formatHint() {
      return '支持图片、PDF、Word（.doc / .docx）';
    },
    /** 与 yi_upload 一致的通用文件上传地址 */
    customerFileUploadUrl() {
      const base =
        process.env.NODE_ENV === 'development'
          ? global.URL_DEV_API_BASE_DOMAIN
          : process.env.VUE_APP_SERVER === 'preview'
            ? global.URL_PRE_API_BASE_DOMAIN
            : global.URL_PROD_API_BASE_DOMAIN;
      return `/web/customer/files/upload?publicly=false`;
    },
    uploadAuthHeaders() {
      return {
        Authorization: localStorage.getItem('token')
      };
    },
    /** 与 Element「点击上传」limit 用法一致：剩余可上传次数；至少为 1（已满时组件已 disabled） */
    uploadLimitRemaining() {
      return Math.max(1, this.maxAttachments - this.fileList.length);
    }
  },
  watch: {
    visible(val) {
      if (val && this.row && this.row.id != null && this.row.id !== '') {
        this.closeEmitted = false;
        this.fetchAttachmentList();
      }
      if (!val) {
        this.fileList = [];
        this.$nextTick(() => {
          if (this.$refs.uploadRef) {
            this.$refs.uploadRef.clearFiles();
          }
        });
      }
    }
  },
  methods: {
    normalizeAttachmentIds(val) {
      if (val == null || val === '' || (Array.isArray(val) && val.length === 0)) {
        return [];
      }
      if (Array.isArray(val)) {
        return val.map(Number).filter(id => !Number.isNaN(id));
      }
      if (typeof val === 'string') {
        return val.split(',').map(s => s.trim()).filter(Boolean).map(Number).filter(id => !Number.isNaN(id));
      }
      const n = Number(val);
      return Number.isNaN(n) ? [] : [n];
    },
    /** 与 list 页一致：GET /web/trial/mould/wo/page?id=，用 rows[0].attachFileIds 作为附件 id 列表再拉文件详情 */
    async fetchAttachmentList() {
      const id = this.row && this.row.id;
      if (id == null || id === '') {
        this.fileList = [];
        return;
      }
      this.loading = true;
      try {
        const res = await this.http.get('/web/trial/mould/wo/page', { params: { id } });
        const listRow = res && res.data && res.data.rows && res.data.rows[0];
        const ids = this.normalizeAttachmentIds(listRow && listRow.attachFileIds);
        if (!ids.length) {
          this.fileList = [];
          return;
        }
        const fr = await this.http.get('/web/customer/files/getFileByIds', { params: { ids } });
        this.fileList = Array.isArray(fr.data) ? fr.data : [];
      } catch (e) {
        this.fileList = [];
      } finally {
        this.loading = false;
      }
    },
    beforeUpload() {
      if (this.fileList.length >= this.maxAttachments) {
        this.$message.warning(this.$t('shuliangyidazuidashangxian'));
        return false;
      }
      return true;
    },
    handleUploadExceed() {
      this.$message.warning(this.$t('shuliangyidazuidashangxian'));
    },
    /** POST /web/trial/mould/wo/uploadAttachFile，仅传试模工单 id 与附件 id 数组 */
    postUploadAttachFile(attachFileIds) {
      if (!this.row || !this.row.id) {
        return Promise.reject(new Error('no row'));
      }
      const ids = Array.isArray(attachFileIds) ? attachFileIds.filter(id => id != null && id !== '') : [];
      return this.http.post('/web/trial/mould/wo/uploadAttachFile', {
        id: this.row.id,
        attachFileIds: ids
      });
    },
    /**
     * 先通用上传拿文件 id，再 uploadAttachFile 绑定附件 id 列表
     */
    customUpload(req) {
      this.loading = true;
      const fd = new FormData();
      fd.append('files', req.file);
      this.http
        .post(this.customerFileUploadUrl, fd, { headers: this.uploadAuthHeaders })
        .then(res => {
          if (!res || res.code !== 200) {
            this.$message.error(res && res.msg ? this.$t(res.msg) : this.$t('caozuoshibai'));
            req.onError(new Error((res && res.msg) || 'upload fail'));
            return null;
          }
          const uploaded = res.data && res.data[0];
          if (!uploaded || uploaded.id == null) {
            this.$message.error(this.$t('caozuoshibai'));
            req.onError(new Error('empty file data'));
            return null;
          }
          const existed = this.collectAttachmentIds();
          const attachFileIds = [...new Set([...existed, uploaded.id])];
          return this.postUploadAttachFile(attachFileIds).then(updateRes => {
            this.$set(this.row, 'attachFileIds', attachFileIds);
            return { uploaded, updateRes };
          });
        })
        .then(result => {
          if (!result) return;
          const { uploaded, updateRes } = result;
          const code = updateRes && updateRes.code;
          if (code !== undefined && code !== 200) {
            this.$message.error(updateRes.msg ? this.$t(updateRes.msg) : this.$t('caozuoshibai'));
            req.onError(new Error(updateRes.msg || 'uploadAttachFile fail'));
            return;
          }
          if (this.fileList.length < this.maxAttachments) {
            this.fileList.push(uploaded);
          }
          this.$message.success(this.$t('caozuochenggong'));
          req.onSuccess(updateRes || { code: 200 });
        })
        .catch(err => {
          this.$message.error(this.$t(err));
          req.onError(err);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    removeAt(index, row) {
      if (this.readOnly || !row) return;
      this.$confirm(this.$t('shifouquerenshanchu'), this.$t('tishi'), {
        confirmButtonText: this.$t('queding'),
        cancelButtonText: this.$t('quxiao'),
        type: 'warning'
      })
        .then(() => {
          this.loading = true;
          const attachFileIds = this.fileList.filter((_, i) => i !== index).map(f => f.id);
          return this.postUploadAttachFile(attachFileIds).then(() => {
            this.$set(this.row, 'attachFileIds', attachFileIds.length ? attachFileIds : null);
          });
        })
        .then(() => {
          this.fileList.splice(index, 1);
          this.$message.success(this.$t('caozuochenggong'));
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return;
          this.$message.error(this.$t(err));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadFile(fileRow) {
      if (!fileRow || fileRow.id == null) return;
      this.http
        .get('/web/customer/files/download', { params: { id: fileRow.id } })
        .then(res => {
          if (res && res.data) {
            window.open(res.data);
          }
        })
        .catch(err => {
          this.$message.error(this.$t(err));
        });
    },
    collectAttachmentIds() {
      return this.fileList.map(f => f.id).filter(id => id != null && id !== '');
    },
    onInnerClose() {
      if (!this.closeEmitted) {
        this.closeEmitted = true;
        this.$emit('closed', {
          row: this.row,
          attachFileIds: this.collectAttachmentIds()
        });
      }
      if (this.visible) {
        this.$emit('update:visible', false);
      }
    }
  }
};
</script>
